import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useTransition,
} from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { FixedSizeList as VirtualList } from "react-window";
import axios from "../../utils/axiosInstance";
import { TextSlicer } from "../../utils/textSlicerDots";

export default function UnassignUsers({ open, onClose, appId }) {
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [stagedForRemoval, setStagedForRemoval] = useState([]);
  const [assignedSearch, setAssignedSearch] = useState("");
  const [stagedSearch, setStagedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [isPending, startTransition] = useTransition();

  const showAlert = useCallback((message, severity = "success") => {
    setAlert({ open: true, message, severity });
  }, []);

  const closeAlert = useCallback(() => {
    setAlert((prev) => ({ ...prev, open: false }));
  }, []);

  // Fetch assigned users only
  useEffect(() => {
    if (!open || !appId) return;

    const loadUsers = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        const res = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/users`,
          {
            params: { companyId: process.env.REACT_APP_COMPANY_ID, appId },
            headers,
          }
        );

        setAssignedUsers(Array.isArray(res.data) ? res.data : []);
        setStagedForRemoval([]);
      } catch (err) {
        console.error(err);
        showAlert("Failed to load users", "error");
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [open, appId, showAlert]);

  // Filter lists
  const filteredAssigned = useMemo(() => {
    const q = assignedSearch.toLowerCase();
    return assignedUsers.filter(
      (u) =>
        u?.name?.toLowerCase().includes(q) ||
        u?.email?.toLowerCase().includes(q) ||
        u?.phone?.toLowerCase().includes(q)
    );
  }, [assignedUsers, assignedSearch]);

  const filteredStaged = useMemo(() => {
    const q = stagedSearch.toLowerCase();
    return stagedForRemoval.filter(
      (u) =>
        u?.name?.toLowerCase().includes(q) ||
        u?.email?.toLowerCase().includes(q) ||
        u?.phone?.toLowerCase().includes(q)
    );
  }, [stagedForRemoval, stagedSearch]);

  // Remove handler
  const handleStageForRemoval = useCallback((user) => {
    if (!user?.id) return;
    startTransition(() => {
      setAssignedUsers((prev) => prev.filter((u) => u.id !== user.id));
      setStagedForRemoval((prev) => [user, ...prev]);
    });
  }, []);

  // Restore handler
  const handleRestore = useCallback((user) => {
    if (!user?.id) return;
    startTransition(() => {
      setStagedForRemoval((prev) => prev.filter((u) => u.id !== user.id));
      setAssignedUsers((prev) => [user, ...prev]);
    });
  }, []);

  // Save changes
  const handleSave = useCallback(async () => {
    if (stagedForRemoval.length === 0) {
      showAlert("No users selected for removal", "warning");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      await axios.put(
        `${process.env.REACT_APP_API_GATEAWAY}/users/updateUsersAppId`,
        { userIds: stagedForRemoval.map((u) => u.id), appId: null },
        { headers }
      );

      showAlert(
        `${stagedForRemoval.length} user(s) removed successfully`,
        "success"
      );
      setStagedForRemoval([]);
      onClose();
    } catch (err) {
      console.error(err);
      showAlert("Error removing users", "error");
    } finally {
      setSaving(false);
    }
  }, [stagedForRemoval, appId, showAlert, onClose]);

  const UserItem = useCallback(
    ({ user, showRemove, showRestore }) => (
      <ListItem
        sx={{
          height: 75,
          border: 1,
          borderColor: showRestore ? "error.main" : "divider",
          borderRadius: 1,
          bgcolor: showRestore ? "error.light" : "background.paper",
          opacity: showRestore ? 0.6 : 1,
        }}
      >
        <ListItemText
          primary={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                fontWeight="bold"
                sx={{
                  fontSize: { xs: "11px", sm: "14px", md: "15px" },
                  textDecoration: showRestore ? "line-through" : "none",
                }}
              >
                {user.name}
              </Typography>
              {showRestore && (
                <Typography
                  variant="caption"
                  color="white"
                  fontWeight="bold"
                  sx={{
                    position: "absolute",
                    top: 5,
                    right: 10,
                    fontSize: { xs: "8px", sm: "12px", md: "14px" },
                  }}
                >
                  (TO REMOVE)
                </Typography>
              )}
            </Box>
          }
          secondary={
            <>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: { xs: "9px", sm: "14px", md: "15px" },
                  // textDecoration: isStagedForRemoval ? "line-through" : "none",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                }}
              >
                {TextSlicer(user.email, 10)}
              </Typography>
              <Typography variant="body2">{user.phone}</Typography>
            </>
          }
        />
        <ListItemSecondaryAction>
          {showRemove && (
            <IconButton
              onClick={() => handleStageForRemoval(user)}
              sx={{
                position: { xs: "absolute", sm: "static", md: "static" },
                top: { xs: 2, sm: "auto", md: "auto" },
                right: { xs: -10, sm: "auto", md: "auto" },
                // XS smaller size
                width: { xs: 28, sm: "auto", md: "auto" },
                height: { xs: 28, sm: "auto", md: "auto" },
                p: { xs: 0.5, sm: 1, md: 1 },
                backgroundColor: "error.main",
                color: "white",
                "&:hover": { backgroundColor: "error.dark" },
              }}
            >
              <DeleteIcon />
            </IconButton>
          )}
          {showRestore && (
            <IconButton
              onClick={() => handleRestore(user)}
              sx={{
                position: { xs: "absolute", sm: "static", md: "static" },
                top: { xs: 2, sm: "auto", md: -10 },
                right: { xs: -10, sm: "auto", md: "auto" },
                // XS smaller size
                width: { xs: 28, sm: "auto", md: "auto" },
                height: { xs: 28, sm: "auto", md: "auto" },
                p: { xs: 0.5, sm: 1, md: 1 },
                backgroundColor: "error.main",
                color: "white",
                "&:hover": { backgroundColor: "error.dark" },
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </ListItemSecondaryAction>
      </ListItem>
    ),
    [handleStageForRemoval, handleRestore]
  );

  const VirtualRow = useCallback(
    ({ index, style, data }) => {
      const user = data.users[index];
      return (
        <div style={style}>
          <UserItem
            user={user}
            showRemove={data.showRemove}
            showRestore={data.showRestore}
          />
        </div>
      );
    },
    [UserItem]
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: { xs: "78%", sm: "60%", md: "100%" } } }}
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight="bold">
              Unassign Users{" "}
              {stagedForRemoval.length > 0 &&
                `(${stagedForRemoval.length} staged for removal)`}
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="400px"
            >
              <CircularProgress color="#60a5fa" size={60} />
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                height: "95%",
                gap: { xs: 0.5, sm: 2, md: 2 },
              }}
            >
              <UserPanel
                title="Currently Assigned"
                users={filteredAssigned}
                search={assignedSearch}
                setSearch={setAssignedSearch}
                VirtualRow={VirtualRow}
                showRemove
              />
              <UserPanel
                title="To Be Unassigned"
                users={filteredStaged}
                search={stagedSearch}
                setSearch={setStagedSearch}
                VirtualRow={VirtualRow}
                showRestore
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{ borderRadius: 0, bgcolor: "grey.400", color: "white" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || stagedForRemoval.length === 0}
            sx={{
              borderRadius: 0,
              bgcolor: "grey.600",
              "&:hover": { bgcolor: "grey.700" },
              "&:disabled": { bgcolor: "grey.300" },
            }}
          >
            {saving ? (
              <>
                <CircularProgress size={20} color="#60a5fa" sx={{ mr: 1 }} />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={closeAlert}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={closeAlert}
          severity={alert.severity}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </>
  );
}

// Reusable panel
const UserPanel = React.memo(
  ({
    title,
    users,
    search,
    setSearch,
    VirtualRow,
    showRemove,
    showRestore,
  }) => {
    const itemData = useMemo(
      () => ({ users, showRemove, showRestore }),
      [users, showRemove, showRestore]
    );
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down("sm"));
    const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));
    const listHeight = isXs ? 320 : isSm ? 310 : 380;
    return (
      <Box sx={{ width: "50%", height: { xs: "90%", sm: "100%", md: "90%" } }}>
        <Paper
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            bgcolor: "grey.800",
          }}
        >
          <Box sx={{ p: 2, bgcolor: "grey.700", color: "white" }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {title}
            </Typography>
          </Box>
          <Box sx={{ p: 1, pb: 1 }}>
            <TextField
              placeholder="SEARCH"
              size="small"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "black" } }}
            />
          </Box>
          <Box sx={{ flex: 1, px: { xs: 1, sm: 2, md: 2 }, pb: 1 }}>
            {users.length > 0 ? (
              <VirtualList
                height={listHeight}
                itemCount={users.length}
                itemSize={82}
                width="100%"
                itemData={itemData}
              >
                {VirtualRow}
              </VirtualList>
            ) : (
              <Typography
                variant="body2"
                color="grey.400"
                textAlign="center"
                sx={{ py: 4 }}
              >
                No users found
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>
    );
  }
);
